import type { AlttvisionApi } from '@shared/types';
import type { IpcChannel, IpcRequest, IpcResponse } from './contracts';

export type ApiChannelInvoker = <C extends IpcChannel>(
  channel: C,
  ...args: IpcRequest<C> extends void ? [] : [IpcRequest<C>]
) => Promise<IpcResponse<C>>;

export function createAlttvisionApiClient(invoke: ApiChannelInvoker): AlttvisionApi {
  return {
    system: {
      getAppInfo: () => invoke('system:getAppInfo'),
      getBootStatus: () => invoke('system:getBootStatus'),
      openExternal: (url) => invoke('system:openExternal', url),
      saveTextFile: (payload) => invoke('system:saveTextFile', payload),
      readClipboardText: () => invoke('system:readClipboardText'),
      writeClipboardText: (payload) => invoke('system:writeClipboardText', payload)
    },
    dashboard: {
      getOverview: () => invoke('dashboard:getOverview')
    },
    search: {
      querySearchIndex: (payload) => invoke('search:querySearchIndex', payload)
    },
    undo: {
      getLastUndoable: () => invoke('undo:getLastUndoable'),
      undoLast: () => invoke('undo:undoLast')
    },
    customers: {
      listCustomers: () => invoke('customers:listCustomers'),
      getCustomerById: (customerId) => invoke('customers:getCustomerById', customerId),
      deleteCustomers: (payload) => invoke('customers:deleteCustomers', payload),
      listDeletedCustomers: () => invoke('customers:listDeletedCustomers'),
      restoreCustomers: (payload) => invoke('customers:restoreCustomers', payload),
      purgeCustomers: (payload) => invoke('customers:purgeCustomers', payload),
      createCustomer: (payload) => invoke('customers:createCustomer', payload),
      updateCustomer: (payload) => invoke('customers:updateCustomer', payload),
      applyBulkPatch: (payload) => invoke('customers:applyBulkPatch', payload)
    },
    leads: {
      listLeads: () => invoke('leads:listLeads'),
      getLeadById: (leadId) => invoke('leads:getLeadById', leadId),
      createLead: (payload) => invoke('leads:createLead', payload),
      updateLead: (payload) => invoke('leads:updateLead', payload),
      applyBulkPatch: (payload) => invoke('leads:applyBulkPatch', payload),
      planContacts: (payload) => invoke('leads:planContacts', payload),
      logLeadActivity: (payload) => invoke('leads:logLeadActivity', payload),
      convertLead: (payload) => invoke('leads:convertLead', payload),
      previewLeadImport: (payload) => invoke('leads:previewLeadImport', payload),
      importLeadsJson: (payload) => invoke('leads:importLeadsJson', payload),
      exportLeadsJson: () => invoke('leads:exportLeadsJson'),
      deleteLeads: (payload) => invoke('leads:deleteLeads', payload),
      listDeletedLeads: () => invoke('leads:listDeletedLeads'),
      restoreLeads: (payload) => invoke('leads:restoreLeads', payload),
      purgeLeads: (payload) => invoke('leads:purgeLeads', payload)
    },
    tasks: {
      listTasks: () => invoke('tasks:listTasks'),
      moveTaskToStatus: (payload) => invoke('tasks:moveTaskToStatus', payload),
      reorderTaskWithinStatus: (payload) => invoke('tasks:reorderTaskWithinStatus', payload),
      deleteTasks: (payload) => invoke('tasks:deleteTasks', payload),
      listDeletedTasks: () => invoke('tasks:listDeletedTasks'),
      restoreTasks: (payload) => invoke('tasks:restoreTasks', payload),
      purgeTasks: (payload) => invoke('tasks:purgeTasks', payload),
      createTask: (payload) => invoke('tasks:createTask', payload),
      updateTask: (payload) => invoke('tasks:updateTask', payload),
      applyBulkPatch: (payload) => invoke('tasks:applyBulkPatch', payload)
    },
    resources: {
      listAssets: () => invoke('resources:listAssets'),
      getAssetById: (assetId) => invoke('resources:getAssetById', assetId),
      createAsset: (payload) => invoke('resources:createAsset', payload),
      importAssets: (payload) => invoke('resources:importAssets', payload),
      updateAsset: (payload) => invoke('resources:updateAsset', payload),
      applyBulkPatch: (payload) => invoke('resources:applyBulkPatch', payload),
      deleteAssets: (payload) => invoke('resources:deleteAssets', payload)
    },
    settings: {
      getAppSettings: () => invoke('settings:getAppSettings'),
      updateAppSettings: (payload) => invoke('settings:updateAppSettings', payload),
      getWorkspaceSettings: () => invoke('settings:getWorkspaceSettings'),
      updateWorkspaceSettings: (payload) => invoke('settings:updateWorkspaceSettings', payload),
      updateWorkspaceLocalEnvironmentSecrets: (payload) => invoke('settings:updateWorkspaceLocalEnvironmentSecrets', payload),
      updateWorkspacePaymentSecrets: (payload) => invoke('settings:updateWorkspacePaymentSecrets', payload),
      updateWorkspaceIntegrationSecrets: (payload) => invoke('settings:updateWorkspaceIntegrationSecrets', payload),
      testOblioConnection: () => invoke('settings:testOblioConnection'),
      refreshOblioNomenclature: () => invoke('settings:refreshOblioNomenclature'),
      getWordPressBridgeStatus: () => invoke('settings:getWordPressBridgeStatus'),
      refreshWordPressBridgeStatus: () => invoke('settings:refreshWordPressBridgeStatus'),
      getWordPressBridgeContract: () => invoke('settings:getWordPressBridgeContract'),
      pickDirectory: (payload) => invoke('settings:pickDirectory', payload),
      listCompanyProfiles: () => invoke('settings:listCompanyProfiles'),
      saveCompanyProfile: (payload) => invoke('settings:saveCompanyProfile', payload),
      listCustomerCategories: () => invoke('settings:listCustomerCategories'),
      saveCustomerCategory: (payload) => invoke('settings:saveCustomerCategory', payload)
    },
    preferences: {
      getUserPreferences: () => invoke('preferences:getUserPreferences'),
      updateUserPreferences: (payload) => invoke('preferences:updateUserPreferences', payload)
    },
    projects: {
      listProjects: () => invoke('projects:listProjects'),
      getProjectById: (projectId) => invoke('projects:getProjectById', projectId),
      applyBulkPatch: (payload) => invoke('projects:applyBulkPatch', payload),
      deleteProjects: (payload) => invoke('projects:deleteProjects', payload),
      listDeletedProjects: () => invoke('projects:listDeletedProjects'),
      restoreProjects: (payload) => invoke('projects:restoreProjects', payload),
      purgeProjects: (payload) => invoke('projects:purgeProjects', payload),
      createProject: (payload) => invoke('projects:createProject', payload),
      updateProject: (payload) => invoke('projects:updateProject', payload),
      createBuilderDocument: (payload) => invoke('projects:createBuilderDocument', payload),
      updateBuilderDocument: (payload) => invoke('projects:updateBuilderDocument', payload),
      deleteBuilderDocument: (payload) => invoke('projects:deleteBuilderDocument', payload),
      setPrimaryBuilderDocument: (payload) => invoke('projects:setPrimaryBuilderDocument', payload),
      updateWordPressAdminCredentials: (payload) => invoke('projects:updateWordPressAdminCredentials', payload),
      attachProjectBrandingAsset: (payload) => invoke('projects:attachProjectBrandingAsset', payload),
      provisionProjectRuntime: (payload) => invoke('projects:provisionProjectRuntime', payload),
      updateProjectRuntime: (payload) => invoke('projects:updateProjectRuntime', payload),
      getProjectRuntimeStatus: (projectId) => invoke('projects:getProjectRuntimeStatus', projectId),
      openProjectLocalUrl: (projectId) => invoke('projects:openProjectLocalUrl', projectId)
    },
    contracts: {
      listContracts: () => invoke('contracts:listContracts'),
      getContractById: (contractId) => invoke('contracts:getContractById', contractId),
      listContractTemplates: () => invoke('contracts:listContractTemplates'),
      getContractTemplateById: (templateId) => invoke('contracts:getContractTemplateById', templateId),
      saveContractTemplate: (payload) => invoke('contracts:saveContractTemplate', payload),
      previewContractGeneration: (payload) => invoke('contracts:previewContractGeneration', payload),
      generateContractFromTemplate: (payload) => invoke('contracts:generateContractFromTemplate', payload),
      deleteContracts: (payload) => invoke('contracts:deleteContracts', payload),
      listDeletedContracts: () => invoke('contracts:listDeletedContracts'),
      restoreContracts: (payload) => invoke('contracts:restoreContracts', payload),
      purgeContracts: (payload) => invoke('contracts:purgeContracts', payload),
      createContract: (payload) => invoke('contracts:createContract', payload),
      updateContract: (payload) => invoke('contracts:updateContract', payload),
      applyBulkPatch: (payload) => invoke('contracts:applyBulkPatch', payload)
    },
    invoices: {
      listInvoices: () => invoke('invoices:listInvoices'),
      getInvoiceById: (invoiceId) => invoke('invoices:getInvoiceById', invoiceId),
      deleteInvoices: (payload) => invoke('invoices:deleteInvoices', payload),
      listDeletedInvoices: () => invoke('invoices:listDeletedInvoices'),
      restoreInvoices: (payload) => invoke('invoices:restoreInvoices', payload),
      purgeInvoices: (payload) => invoke('invoices:purgeInvoices', payload),
      createInvoice: (payload) => invoke('invoices:createInvoice', payload),
      updateInvoice: (payload) => invoke('invoices:updateInvoice', payload),
      applyBulkPatch: (payload) => invoke('invoices:applyBulkPatch', payload),
      replaceInvoiceLineItems: (payload) => invoke('invoices:replaceInvoiceLineItems', payload),
      syncInvoiceToOblio: (payload) => invoke('invoices:syncInvoiceToOblio', payload)
    },
    payments: {
      listPaymentOrders: () => invoke('payments:listPaymentOrders'),
      getPaymentOrderById: (paymentOrderId) => invoke('payments:getPaymentOrderById', paymentOrderId),
      createPaymentOrder: (payload) => invoke('payments:createPaymentOrder', payload),
      updatePaymentOrder: (payload) => invoke('payments:updatePaymentOrder', payload),
      syncPaymentOrder: (payload) => invoke('payments:syncPaymentOrder', payload),
      refreshPaymentOrder: (payload) => invoke('payments:refreshPaymentOrder', payload),
      deletePaymentOrders: (payload) => invoke('payments:deletePaymentOrders', payload),
      listDeletedPaymentOrders: () => invoke('payments:listDeletedPaymentOrders'),
      restorePaymentOrders: (payload) => invoke('payments:restorePaymentOrders', payload),
      purgePaymentOrders: (payload) => invoke('payments:purgePaymentOrders', payload)
    },
    ai: {
      listModels: () => invoke('ai:listModels'),
      getProviderKeyStatus: () => invoke('ai:getProviderKeyStatus'),
      setProviderKey: (payload) => invoke('ai:setProviderKey', payload),
      testProviderKey: (payload) => invoke('ai:testProviderKey', payload),
      generateBuilderPage: (payload) => invoke('ai:generateBuilderPage', payload),
      editBuilderSection: (payload) => invoke('ai:editBuilderSection', payload),
      clearCache: () => invoke('ai:clearCache')
    }
  };
}
